/**
 * ConfirmDialog + PromptDialog — replacements for `window.confirm()`
 * and `window.prompt()` in destructive flows. The native browser
 * variants render as ugly inline white-on-black bars on iOS Safari,
 * don't respect RTL, and have no styling hooks for the rest of our
 * Tailwind theme. These do.
 *
 * Usage pattern: each consuming page owns the open/close state +
 * the action callback; the dialog only handles render + keyboard
 * (Escape closes; Enter on the input submits the prompt).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './Button';
import { TextField } from './TextField';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Yes/no confirmation. Use for destructive actions (cancel, archive,
 * remove). The destructive button keeps `Button variant="primary"` but
 * goes red via the `destructive` prop.
 */
export function ConfirmDialog({
  open, title, body, confirmLabel, cancelLabel,
  destructive, loading, errorMessage, onConfirm, onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  useEscapeKey(open, onCancel);
  if (!open) return null;
  return (
    <Backdrop onCancel={onCancel}>
      <div className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          {destructive && (
            <div className="bg-error/10 rounded-full p-2 flex-shrink-0">
              <AlertTriangle size={20} className="text-error" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-text-primary mb-1">{title}</h3>
            {body && <p className="text-sm text-text-secondary leading-relaxed">{body}</p>}
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-text-tertiary hover:text-text-primary p-1 -mt-1 -mr-1 disabled:opacity-50"
            aria-label="close"
          >
            <X size={18} />
          </button>
        </div>
        {errorMessage && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-3">{errorMessage}</div>
        )}
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel} disabled={loading} type="button">
            {cancelLabel ?? t('cancel', 'إلغاء')}
          </Button>
          <Button
            variant="primary"
            loading={loading}
            onClick={onConfirm}
            type="button"
            className={destructive ? 'bg-error hover:bg-error/90 disabled:bg-error/50' : undefined}
          >
            {confirmLabel ?? t('confirm', 'تأكيد')}
          </Button>
        </div>
      </div>
    </Backdrop>
  );
}

interface PromptDialogProps {
  open: boolean;
  title: string;
  body?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  multiline?: boolean;
  required?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  errorMessage?: string | null;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

/**
 * Single-field text prompt. Use for "reason for rejection", "seal
 * number", etc. Multiline switches to `<textarea>` for longer answers.
 */
export function PromptDialog({
  open, title, body, inputLabel, inputPlaceholder,
  multiline, required, confirmLabel, cancelLabel,
  destructive, loading, errorMessage, onConfirm, onCancel,
}: PromptDialogProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEscapeKey(open, onCancel);

  // Reset value + autofocus on open
  useEffect(() => {
    if (open) {
      setValue('');
      // setTimeout delays focus past the React render so the input is mounted
      const t = setTimeout(() => {
        if (multiline) textareaRef.current?.focus();
        else inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open, multiline]);

  if (!open) return null;

  const submit = () => {
    if (required && !value.trim()) return;
    onConfirm(value);
  };

  return (
    <Backdrop onCancel={onCancel}>
      <form
        className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-text-primary mb-1">{title}</h3>
            {body && <p className="text-sm text-text-secondary leading-relaxed">{body}</p>}
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            type="button"
            className="text-text-tertiary hover:text-text-primary p-1 -mt-1 -mr-1 disabled:opacity-50"
            aria-label="close"
          >
            <X size={18} />
          </button>
        </div>
        {multiline ? (
          <label className="block">
            {inputLabel && <span className="text-xs text-text-secondary mb-1 block">{inputLabel}</span>}
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={inputPlaceholder}
              required={required}
              className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary min-h-[100px]"
            />
          </label>
        ) : (
          <TextField
            ref={inputRef}
            label={inputLabel}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={inputPlaceholder}
            required={required}
          />
        )}
        {errorMessage && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mt-3">{errorMessage}</div>
        )}
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="ghost" onClick={onCancel} disabled={loading} type="button">
            {cancelLabel ?? t('cancel', 'إلغاء')}
          </Button>
          <Button
            variant="primary"
            loading={loading}
            disabled={required && !value.trim()}
            type="submit"
            className={destructive ? 'bg-error hover:bg-error/90 disabled:bg-error/50' : undefined}
          >
            {confirmLabel ?? t('confirm', 'تأكيد')}
          </Button>
        </div>
      </form>
    </Backdrop>
  );
}

function Backdrop({ children, onCancel }: { children: React.ReactNode; onCancel: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      {children}
    </div>
  );
}

function useEscapeKey(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
}
